import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaShoppingCart } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { findCategoryHierarchy } from "../utils/findCategoryHierarchy";

const ReadMoreList = ({ items, field, label, showMore, toggleShowMore }) => {
  if (!items || items.length === 0) return null;

  const maxItems = 4;
  const shouldShowReadMore = items.length > maxItems;
  const displayedItems = showMore[field] ? items : items.slice(0, maxItems);

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{label}</h2>
      <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1.5">
        {displayedItems.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
      {shouldShowReadMore && (
        <button
          onClick={() => toggleShowMore(field)}
          className="text-[#35A93E] font-medium hover:underline mt-2 text-sm"
        >
          {showMore[field] ? "Show Less ▲" : "Read More ▼"}
        </button>
      )}
    </div>
  );
};

const VariationOptions = ({
  variations,
  selectedVariation,
  onSelectVariation,
}) => {
  if (!variations?.length) return null;

  return (
    <div className="mb-6">
      <h3 className="font-semibold text-gray-900 mb-3 text-base">
        Select Variation
      </h3>
      <div className="flex flex-wrap gap-3">
        {variations.map((variation, index) => (
          <button
            key={index}
            onClick={() => onSelectVariation(variation)}
            className={`px-5 py-3 text-sm font-medium rounded-2xl border-2 transition-all ${
              selectedVariation?._id === variation._id
                ? "border-[#35A93E] bg-[#35A93E] text-white"
                : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
            }`}
          >
            Size {variation.size} • Color {variation.color}
          </button>
        ))}
      </div>
    </div>
  );
};

const DetailItem = ({ label, value }) => {
  if (!value || value === "N/A" || value === "") return null;
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0 text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
};

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { product, category, subCategory, subSubCategory, subSubSubCategory } =
    location.state || {};

  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${base_url}product/getCategory?page=1&limit=1000&sortingColumn=createdAt&sortingOrder=asc`,
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setCategories(data?.data || []);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const hierarchy = findCategoryHierarchy(categories, {}, id);

  const displayProduct =
    product ||
    categories
      ?.flatMap((cat) =>
        (cat.subCategories || []).flatMap((subCat) => [
          ...(subCat.products || []),
          ...(subCat.subSubCategories || []).flatMap((subSubCat) => [
            ...(subSubCat.products || []),
            ...(subSubCat.subSubSubCategories || []).flatMap(
              (subSubSubCat) => subSubSubCat.products || [],
            ),
          ]),
        ]),
      )
      .find((p) => p._id === id);

  useEffect(() => {
    if (displayProduct && !product && !loading) {
      navigate(`/product-details/${id}`, {
        state: {
          product: displayProduct,
          category: hierarchy.category,
          subCategory: hierarchy.subCategory,
          subSubCategory: hierarchy.subSubCategory,
          subSubSubCategory: hierarchy.subSubSubCategory,
        },
        replace: true,
      });
    }
  }, [displayProduct, product, loading, navigate, id, hierarchy]);

  useEffect(() => {
    if (!displayProduct && !loading) {
      navigate("/", { replace: true });
    }
  }, [displayProduct, loading, navigate]);

  const [selectedVariation, setSelectedVariation] = useState(
    displayProduct?.variations?.[0] || null,
  );
  const [selectedImage, setSelectedImage] = useState(
    displayProduct?.variations?.[0]?.images?.[0] ||
      displayProduct?.images?.[0] ||
      null,
  );

  const [showMore, setShowMore] = useState({
    benefits: false,
    features: false,
    instructionsForUse: false,
    batteryInfo: false,
    operatingConditions: false,
    packageContents: false,
    precautions: false,
  });

  const [token, setToken] = useState(() =>
    localStorage.getItem("user_website")
      ? JSON.parse(localStorage.getItem("user_website"))
      : null,
  );

  useEffect(() => {
    const handleStorageChange = () => {
      const user = localStorage.getItem("user_website");
      setToken(user ? JSON.parse(user) : null);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const mappedProduct = useMemo(
    () => ({
      _id: displayProduct?._id,
      productName: displayProduct?.productName,
      briefDescription: displayProduct?.briefDescription,
      benefits: displayProduct?.benefits,
      features: displayProduct?.features,
      instructionsForUse: displayProduct?.instructionsForUse,
      batteryInfo: displayProduct?.batteryInfo,
      operatingConditions: displayProduct?.operatingConditions,
      packageContents: displayProduct?.packageContents,
      precautions: displayProduct?.precautions,
      cost: {
        min:
          selectedVariation?.price?.amount ||
          displayProduct?.price?.amount ||
          displayProduct?.cost?.min,
      },
      image: selectedVariation?.images || displayProduct?.images,
      packageType: displayProduct?.packageType || "Standard",
      delivery: displayProduct?.delivery || "5-7 business days",
      size: selectedVariation?.size || displayProduct?.size || "N/A",
      rfidNfcCard: displayProduct?.rfidNfcCard || "Yes",
      activation: displayProduct?.activation || "Instant",
      material: displayProduct?.material,
      weight: selectedVariation?.weight,
      dimensions: selectedVariation?.dimensions,
      brandName: displayProduct?.brandName,
      manufacturerName: displayProduct?.manufacturerName,
      hsnCode: displayProduct?.hsnCode,
      duration_days: displayProduct?.duration_days,
      powerRequirements: displayProduct?.powerRequirements,
      productType: displayProduct?.productType || "physical",
    }),
    [displayProduct, selectedVariation],
  );

  const handleVariationChange = useCallback(
    (variation) => {
      setSelectedVariation(variation);
      setSelectedImage(variation?.images?.[0] || displayProduct?.images?.[0]);
    },
    [displayProduct],
  );

  const toggleShowMore = useCallback((field) => {
    setShowMore((prev) => ({ ...prev, [field]: !prev[field] }));
  }, []);

  const addToCart = useCallback(async () => {
    try {
      if (!token?.token) {
        toast.error("Please log in to add items to cart");
        return;
      }

      const isWalletUser = token?.userType === "member";
      if (
        isWalletUser &&
        [
          "686bc4919224b59b776bf19e",
          "686bc5f09224b59b776bf1a8",
          "686d10b27dcf8686e68ebe09",
          "6877a12b7401d33df403bad7",
        ].includes(displayProduct?._id)
      ) {
        toast.error(
          "Members who already have a package at signup cannot purchase another package.",
        );
        return;
      }

      const cartUrl = isWalletUser
        ? `${base_url}product/addtocartMember`
        : `${base_url}product/addtoCart`;

      const cartBody = {
        productSource: "company",
        productId: displayProduct?._id,
        subCategoryId: subCategory || hierarchy.subCategory?._id,
        categoryId: category || hierarchy.category?._id,
        subSubCategoryId: subSubCategory || hierarchy.subSubCategory?._id,
        subSubSubCategoryId:
          subSubSubCategory || hierarchy.subSubSubCategory?._id,
        variationId: selectedVariation?._id,
        quantity: 1,
      };

      const response = await fetch(cartUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.token}`,
        },
        body: JSON.stringify(cartBody),
      });

      const data = await response.json();
      if (!response.ok)
        throw new Error(data?.message || "Failed to add to cart");

      toast.success(data?.message || "Added to cart!");
      navigate("/cart");
    } catch (error) {
      toast.error(error.message || "Failed to add to cart");
    }
  }, [
    token,
    displayProduct,
    category,
    subCategory,
    subSubCategory,
    subSubSubCategory,
    selectedVariation,
    hierarchy,
    navigate,
  ]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Error: {error}
      </div>
    );
  if (!displayProduct) return null;

  const isPhysical = mappedProduct.productType === "physical";

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Sticky Image */}
          <div className="lg:sticky lg:top-8 self-start">
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-100">
              <div className="h-[460px] flex items-center justify-center p-8 bg-gray-50">
                <img
                  src={selectedImage || mappedProduct.image?.[0]}
                  alt={mappedProduct.productName}
                  className="w-full h-full object-contain transition-transform duration-200 ease-in-out hover:scale-150"
                  loading="lazy"
                />
              </div>
              <div>
                <div className="bg-white rounded-2xl p-5 space-y-2 border border-gray-100">
                  <DetailItem label="Brand" value={mappedProduct.brandName} />
                  <DetailItem
                    label="Manufacturer"
                    value={mappedProduct.manufacturerName}
                  />
                  <DetailItem label="HSN Code" value={mappedProduct.hsnCode} />
                  <DetailItem
                    label="Package Type"
                    value={mappedProduct.packageType}
                  />
                  <DetailItem
                    label="Delivery Time"
                    value={mappedProduct.delivery}
                  />
                  <DetailItem
                    label="RFID/NFC Card"
                    value={mappedProduct.rfidNfcCard}
                  />
                  <DetailItem
                    label="Activation"
                    value={mappedProduct.activation}
                  />
                </div>
              </div>
            </div>

            {mappedProduct.image?.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                {mappedProduct.image.map((img, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedImage(img)}
                    className={`w-20 h-20 flex-shrink-0 border-2 rounded-2xl overflow-hidden cursor-pointer transition-all ${
                      selectedImage === img
                        ? "border-[#35A93E] shadow"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-contain bg-white p-1"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-2">
            <h1 className="text-xl font-bold text-gray-900">
              {mappedProduct.productName}
            </h1>
            <p className="text-gray-600 leading-relaxed text-[15px]">
              {mappedProduct.briefDescription}
            </p>

            {/* Price + Add to Cart */}
            <div className="flex items-center justify-between bg-white p-1">
              <div className="text-xl font-semibold text-gray-900">
                {getCurrencySymbol()}
                {convertPrice(mappedProduct.cost.min)}
              </div>
              <button
                onClick={addToCart}
                className="bg-[#35A93E] hover:bg-[#2e8b35] text-white font-semibold px-4 py-3.5 rounded-xl flex items-center gap-2 transition shadow"
              >
                <FaShoppingCart /> Add to Cart
              </button>
            </div>

            <VariationOptions
              variations={displayProduct?.variations}
              selectedVariation={selectedVariation}
              onSelectVariation={handleVariationChange}
            />

            <ReadMoreList
              items={mappedProduct.benefits}
              field="benefits"
              label="Product Benefits"
              showMore={showMore}
              toggleShowMore={toggleShowMore}
            />

            {mappedProduct.productType === "digital" && (
              <ReadMoreList
                items={mappedProduct.features}
                field="features"
                label="Key Features"
                showMore={showMore}
                toggleShowMore={toggleShowMore}
              />
            )}

            <ReadMoreList
              items={mappedProduct.instructionsForUse}
              field="instructionsForUse"
              label="Instructions for Use"
              showMore={showMore}
              toggleShowMore={toggleShowMore}
            />

            {isPhysical && (
              <>
                <ReadMoreList
                  items={mappedProduct.batteryInfo}
                  field="batteryInfo"
                  label="Battery Information"
                  showMore={showMore}
                  toggleShowMore={toggleShowMore}
                />
                <ReadMoreList
                  items={mappedProduct.packageContents}
                  field="packageContents"
                  label="Package Contents"
                  showMore={showMore}
                  toggleShowMore={toggleShowMore}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
